import { type Masked, unmask } from '~/contentful/graphql';
import { ArticleType } from '~/contentful/model/articleType';
import { ContentType } from '~/contentful/model/contentType';
import { AnalyticsCard, AnalyticsContent } from '~/ui/components/analytics';
import { BaseCard, CardSize, type CommonCardProps } from '~/ui/components/cards/base';
import { VoicesCard } from '~/ui/components/cards/voices';
import { isInternalLink } from '~/ui/components/link/utils';
import { getInternalLink, getInternalLinkFromFragment } from '~/ui/systems/internalLink';
import { formatDate } from '~/ui/utils/formatDate';

import { type ArticleCard_ArticleFragment } from './query';

export interface ArticleCardProps extends CommonCardProps {
  data: Masked<typeof ArticleCard_ArticleFragment>;
  /**
   * Allows specifying a smaller card size (e.g. for carousels)
   * @default {@link CardSize CardSize.LARGE}
   */
  size?: CardSize;
  /** @default true */
  showAllEventsCta?: boolean;
  /** @default "Read the article" */
  cta?: string;
}

export function ArticleCard({
  data,
  size = CardSize.DEFAULT,
  module,
  showAllEventsCta = true,
  cta = 'Read the article',
  ...props
}: ArticleCardProps) {
  const article = unmask<typeof ArticleCard_ArticleFragment>(data);

  switch (article.type) {
    case ArticleType.GENERIC:
    case ArticleType.STORY:
      return (
        <BaseCard
          eyebrow={article.eyebrow ?? ''}
          title={article.title ?? ''}
          cta={cta}
          href={getInternalLinkFromFragment(article)}
          hero={article.hero ?? article.heroImage ?? undefined}
          size={size}
          event={{
            cardModule: module,
            cardContentType: AnalyticsContent.ARTICLE,
            cardType: AnalyticsCard.ARTICLE,
          }}
          truncateTitle={3}
          {...props}
        />
      );

    case ArticleType.MELLON_IN_THE_NEWS:
    case ArticleType.GRANTEE:
      const href = getInternalLinkFromFragment(article);
      return (
        <BaseCard
          logo={article.publisherLogo ?? undefined}
          eyebrow={formatDate(article.date.replace(/-/g, '/').replace(/T.+/, ''))}
          title={article.title ?? ''}
          cta="Read the news"
          href={href}
          hero={article.hero ?? article.heroImage ?? undefined}
          event={{
            cardModule: module,
            cardType: AnalyticsCard.NEWS,
            cardContentType: isInternalLink(href)
              ? AnalyticsContent.INTERNAL_NEWS
              : AnalyticsContent.EXTERNAL_NEWS,
          }}
          size={size}
          truncateTitle={3}
          {...props}
        />
      );

    case ArticleType.VOICES:
      const internalAuthor = article.person
        ? {
            name: article.person.name ?? '',
            title: article.person.jobTitle ?? '',
            organization: article.person.departmentOrGrantMakingArea?.name ?? '',
            image: article.person.image ?? undefined,
          }
        : undefined;
      const externalAuthor = article.externalPerson
        ? {
            name: article.externalPerson.name ?? '',
            title: article.externalPerson.jobTitle ?? '',
            organization: article.externalPerson.organization ?? '',
            image: article.externalPerson.image ?? undefined,
          }
        : undefined;
      return (
        <VoicesCard
          title={article.title ?? ''}
          href={getInternalLinkFromFragment(article)}
          isExternal={!internalAuthor}
          author={internalAuthor || externalAuthor}
          {...props}
        />
      );

    case ArticleType.PAST_EVENT:
      return (
        <BaseCard
          eyebrow="Past event"
          title={article.title ?? ''}
          hero={article.hero ?? article.heroImage ?? undefined}
          cta="Watch event"
          href={getInternalLinkFromFragment(article)}
          size={size}
          secondaryCta={showAllEventsCta ? 'View all events' : undefined}
          secondaryHref={
            showAllEventsCta ? getInternalLink(ContentType.EVENTS_LANDING_PAGE) : undefined
          }
          event={{
            cardContentType: AnalyticsContent.PAST_EVENT,
            cardType: AnalyticsCard.EVENT,
            cardModule: module,
          }}
          truncateTitle={4}
          {...props}
        />
      );

    case ArticleType.REPORT:
      return (
        <BaseCard
          cta="Read the report"
          href={getInternalLinkFromFragment(article)}
          size={size}
          title={article.title ?? ''}
          eyebrow="Report"
          hero={article.hero ?? article.heroImage ?? undefined}
          truncateTitle={3}
          {...props}
        />
      );
  }
}
